<template lang="pug">
v-container(fluid)
  v-card
    v-card-title.d-flex.justify-space-between
      span Gestión de Marcas
      v-btn(color="primary" @click="openDialog()")
        v-icon(left) mdi-plus
        | Nueva Marca

    v-card-text
      // Search and filters
      v-row.mb-4
        v-col(cols="12" sm="6" md="4")
          v-text-field(
            v-model="search"
            label="Buscar marca"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
          )
        v-col(cols="12" sm="6" md="4")
          v-select(
            v-model="statusFilter"
            :items="statusOptions"
            label="Estado"
            outlined
            dense
            clearable
          )

      // Brands table
      v-data-table(
        :headers="headers"
        :items="filteredBrands"
        :loading="loading"
        :search="search"
      )
        template(v-slot:item.logo="{ item }")
          v-avatar(size="40")
            v-img(:src="item.logo" :alt="item.name")
        template(v-slot:item.active="{ item }")
          v-chip(
            :color="item.active ? 'success' : 'error'"
            small
          ) {{ item.active ? 'Activo' : 'Inactivo' }}

        template(v-slot:item.createdAt="{ item }")
          | {{ formatDate(item.createdAt) }}

        template(v-slot:item.actions="{ item }")
          v-btn(
            icon
            small
            class="mr-2"
            @click="openDialog(item)"
          )
            v-icon mdi-pencil
          v-btn(
            icon
            small
            color="error"
            @click="confirmDelete(item)"
          )
            v-icon mdi-delete

  // Brand Dialog
  v-dialog(v-model="dialog" max-width="500")
    v-card
      v-card-title {{ editedItem._id ? 'Editar' : 'Nueva' }} Marca
      v-card-text
        v-form(ref="form" v-model="valid")
          v-text-field(
            v-model="editedItem.name"
            label="Nombre"
            :rules="nameRules"
            required
          )

          v-textarea(
            v-model="editedItem.description"
            label="Descripción"
            rows="3"
          )

          v-file-input(
            v-model="logoFile"
            :rules="logoRules"
            accept="image/*"
            label="Logo"
            prepend-icon="mdi-camera"
            :required="!editedItem._id"
            show-size
            :loading="imageUploading"
            :disabled="imageUploading"
            @change="handleLogoUpload"
          )

          v-img(
            v-if="editedItem.logo"
            :src="editedItem.logo"
            max-height="200"
            contain
            class="grey lighten-2 mb-4"
          )

          v-switch(
            v-model="editedItem.active"
            label="Activa"
            color="success"
          )

      v-card-actions
        v-spacer
        v-btn(text @click="closeDialog") Cancelar
        v-btn(
          color="primary"
          :disabled="!valid || imageUploading"
          :loading="imageUploading && !editedItem._id"
          @click="save"
        ) Guardar

  // Delete Confirmation Dialog
  v-dialog(v-model="deleteDialog" max-width="400")
    v-card
      v-card-title Eliminar Marca
      v-card-text
        | ¿Estás seguro de eliminar la marca "{{ brandToDelete?.name }}"?
        | Esta acción no se puede deshacer.
      v-card-actions
        v-spacer
        v-btn(text @click="deleteDialog = false") Cancelar
        v-btn(
          color="error"
          @click="deleteBrand"
        ) Eliminar
</template>

<script>
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'BrandsManager',

  data: () => ({
    loading: false,
    dialog: false,
    deleteDialog: false,
    valid: false,
    search: '',
    logoFile: null,
    imageUploading: false,
    previewUrl: null,
    statusFilter: null,
    headers: [
      { text: 'Logo', value: 'logo', sortable: false },
      { text: 'Nombre', value: 'name' },
      { text: 'Descripción', value: 'description' },
      { text: 'Estado', value: 'active' },
      { text: 'Fecha de creación', value: 'createdAt' },
      { text: 'Acciones', value: 'actions', sortable: false }
    ],
    editedItem: {
      name: '',
      description: '',
      logo: '',
      active: true
    },
    brandToDelete: null,
    nameRules: [
      v => !!v || 'El nombre es requerido'
    ],
    logoRules: [
      v => !v || v.size < 5000000 || 'El logo debe ser menor a 5MB'
    ],
    statusOptions: [
      { text: 'Activas', value: true },
      { text: 'Inactivas', value: false }
    ]
  }),

  computed: {
    brands() {
      return this.$store.state.brands.list
    },

    filteredBrands() {
      return this.brands.filter(brand => {
        if (this.statusFilter !== null && brand.active !== this.statusFilter) {
          return false
        }
        return true
      })
    }
  },

  methods: {
    async handleLogoUpload(file) {
      if (!file) {
        this.logoFile = null
        this.previewUrl = null
        return
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$toast.error('Por favor selecciona una imagen válida')
        this.logoFile = null
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error('La imagen debe ser menor a 5MB')
        this.logoFile = null
        return
      }

      // Store file for upload
      this.logoFile = file

      // Upload image immediately
      try {
        this.imageUploading = true
        const formData = new FormData()
        formData.append('logo', file)  // El backend de brands espera 'logo', no 'image'
        const data = await this.$store.dispatch('brands/uploadLogo', formData)
        this.editedItem.logo = data.url
        this.previewUrl = data.url
      } catch (error) {
        console.error('Error subiendo imagen:', error)
        this.$toast.error('Error al subir la imagen')
      } finally {
        this.imageUploading = false
      }
    },
    formatDate(date) {
      return format(new Date(date), 'dd/MM/yyyy', { locale: es })
    },

    handleFileChange(file) {
      // Store the file object
      this.logoFile = file
    },

    openDialog(item = null) {
      this.editedItem = item
        ? { ...item }
        : {
            name: '',
            description: '',
            logo: '',
            active: true
          }
      this.logoFile = null
      this.dialog = true
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.$refs.form.reset()
        this.editedItem = {
          name: '',
          description: '',
          logo: '',
          active: true
        }
        this.logoFile = null
        this.previewUrl = null
      })
    },

    async save() {
      if (!this.$refs.form.validate()) return
      if (this.imageUploading) {
        this.$toast.error('Esperá a que termine de subir el logo antes de guardar')
        return
      }

      try {
        const brandData = {
          name: this.editedItem.name,
          description: this.editedItem.description || '',
          logo: this.editedItem.logo,
          active: this.editedItem.active
        }

        if (this.editedItem._id) {
          // Actualizar marca existente
          brandData._id = this.editedItem._id
          await this.$store.dispatch('brands/update', brandData)
          this.$toast.success('Marca actualizada')
        } else {
          // Crear nueva marca
          await this.$store.dispatch('brands/create', brandData)
          this.$toast.success('Marca creada')
        }
        this.closeDialog()
      } catch (error) {
        console.error('Error guardando marca:', error)
        this.$toast.error('Error al guardar la marca: ' + (error.message || 'Error desconocido'))
      }
    },

    confirmDelete(brand) {
      this.brandToDelete = brand
      this.deleteDialog = true
    },

    async deleteBrand() {
      try {
        await this.$store.dispatch('brands/delete', this.brandToDelete._id)
        this.$toast.success('Marca eliminada')
        this.deleteDialog = false
        this.brandToDelete = null
      } catch (error) {
        this.$toast.error('Error al eliminar la marca')
      }
    }
  },

  async created() {
    this.loading = true
    try {
      await this.$store.dispatch('brands/fetch')
    } catch (error) {
      this.$toast.error('Error al cargar las marcas')
    } finally {
      this.loading = false
    }
  }
}
</script>
